import React from 'react';
import {view, Spinner, tokens} from '@sail/ui';
import type {CssProperties} from '@sail/ui';
import {Text, TextType} from 'src/v2/components/common/Text';
import {MERCHANT_BRANDING_DEFAULTS} from 'src/v2/util/merchantBrandingDefaults';
import {getAccessibleTextColor} from 'src/v2/util/getAccessibleTextColor';

// Common shadow values for button states
const BASE_SHADOW =
  'inset 0 0 0 1px rgba(50, 50, 93, 0.1), 0 6px 15px 0 rgba(50, 50, 93, 0.2), 0 2px 2px 0 rgba(0, 0, 0, 0.1)';
const FOCUS_RING = '0 0 0 4px rgba(50, 151, 211, 0.3)';

type ButtonProps = {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  css?: CssProperties;
  textCss?: CssProperties;
  type?: 'primary' | 'secondary';
  textType?: TextType;
};

export const Button = ({
  children,
  onClick,
  disabled,
  loading,
  type = 'primary',
  css,
  textCss,
  textType = 'bodyEmphasized',
}: ButtonProps) => {
  const primaryBackgroundColor =
    (css?.backgroundColor as string) || MERCHANT_BRANDING_DEFAULTS.buttonColor;
  const buttonVariant =
    type === 'primary'
      ? {
          backgroundColor: primaryBackgroundColor,
          color: getAccessibleTextColor(primaryBackgroundColor),
          hoverBackgroundColor: primaryBackgroundColor,
        }
      : {
          backgroundColor: 'transparent',
          color: 'subdued',
          hoverBackgroundColor: tokens.color.neutral[50].toString(),
        };
  const isDisabled = disabled || loading;

  return (
    <view.button
      onClick={onClick}
      disabled={isDisabled}
      css={{
        width: 'fill',
        backgroundColor: buttonVariant.backgroundColor,
        borderRadius: 'small',
        border: 'none',
        padding: 'space.150',
        transition: 'all 250ms ease-in-out',
        fontFamily: 'inherit',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        color: buttonVariant.color,
        ':hover': !isDisabled
          ? {
              cursor: 'pointer',
              opacity: 0.9,
              backgroundColor: buttonVariant.hoverBackgroundColor,
            }
          : {},
        ':focus': !isDisabled
          ? {
              outline: 'none',
              boxShadow: `${BASE_SHADOW}, ${FOCUS_RING}`,
            }
          : {},
        ...css,
      }}
    >
      <Text
        type={textType}
        css={{
          color: 'inherit',
          opacity: isDisabled ? 0.5 : undefined,
          ...textCss,
        }}
      >
        {children}
      </Text>
      {loading ? (
        <Spinner
          css={{
            width: 'medium',
            height: 'medium',
            color: 'inherit',
            opacity: isDisabled ? 0.5 : undefined,
            position: 'absolute',
            right: 'space.500',
          }}
        />
      ) : null}
    </view.button>
  );
};
